import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — a single-step tumble on a HIGH-pay (H2) cluster, for visual variety against the
 * low-symbol tumble fixtures (the H symbols cross-fade to their alt win frame as they highlight).
 * One reveal win → remove → one collapse drop-in (no further win) → end. Cluster/mask win, no payline.
 */
export function baseFixtureReelTumbleH(): SpinResponse {
  const win = mask([10, 11, 15, 16]); // H2 ×4 (bottom-left) — the one HIGH cluster in an L-heavy grid

  return spinFixture("reel-tumble-h")
    .balance("EUR", "100000")
    // --- seq 0: reveal — H2 cluster wins, removed ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("300").gridRows([
        ["L3", "L5", "L2", "L4", "L1"],
        ["L5", "L3", "L5", "L2", "L5"],
        ["H2", "H2", "L4", "L5", "L1"],
        ["H2", "H2", "L1", "L3", "L4"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win, winAmount: "300", symbolIdentifier: "H2", symbolCount: "4", // 150 × 2
        lines: [{ positionMask: win, winAmount: "300", baseWinAmount: "150", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win" });

      const fire = cannonFire(0);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire" });

      const r = fx.removeSymbolsByMask(win);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove" });

      const t = fx.updateTotalWinMeter("300");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total" });
    })
    // --- seq 1: collapse — no further win, round ends ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("300").gridRows([
        ["L2", "L4", "L2", "L4", "L1"],
        ["L1", "L2", "L5", "L2", "L5"],
        ["L3", "L5", "L4", "L5", "L1"],
        ["L5", "L3", "L1", "L3", "L4"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", "300");
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
